export const articles = [
  {
    id: 'mahjong-beginner-guide',
    title: '麻将入门完全指南：从零开始学会打麻将',
    excerpt: '想学麻将但不知道从何入手？这篇指南从牌型识别、基本规则到胡牌技巧，手把手教你成为麻将高手。',
    content: `麻将是中国最受欢迎的桌游之一，但对于新手来说，复杂的规则可能让人望而却步。别担心，这篇指南将带你从零开始，逐步掌握麻将的精髓。

## 认识麻将牌

一副标准麻将共有144张牌，分为以下几类：

### 序数牌（108张）
- **万子**：一万到九万，各4张，共36张
- **条子**：一条到九条，各4张，共36张
- **筒子**：一筒到九筒，各4张，共36张

### 字牌（28张）
- **风牌**：东、南、西、北，各4张，共16张
- **箭牌**：中、发、白，各4张，共12张

### 花牌（8张）
- 春、夏、秋、冬、梅、兰、竹、菊，各1张

## 基本规则

麻将的基本目标是凑成特定的牌型（称为"胡牌"）。标准胡牌牌型是4组"面子"加1对"雀头"。

### 面子类型
- **顺子**：同花色连续的三张牌，如一萬+二萬+三萬
- **刻子**：三张相同的牌，如三个红中
- **杠子**：四张相同的牌（需开杠）

### 基本动作
- **摸牌**：从牌堆中取一张牌
- **打牌**：从手牌中打出一张牌
- **吃牌**：用上家打出的牌组成顺子
- **碰牌**：用任意玩家打出的牌组成刻子
- **杠牌**：用任意玩家打出的牌组成杠子，或自己摸到第四张
- **胡牌**：手牌满足胡牌条件时宣布获胜

## 游戏流程

1. **洗牌与码牌**：将所有牌洗匀，码成牌墙
2. **掷骰子**：确定庄家和起牌位置
3. **抓牌**：每人抓13张牌（庄家14张）
4. **行牌**：按逆时针顺序，每人摸一张打一张
5. **胡牌**：有人胡牌或牌被摸完则一局结束

## 新手常见错误

1. **不看桌面**：只顾自己的牌，忽略其他玩家打出的牌
2. **贪大求全**：追求大牌型而错过胡牌机会
3. **不会弃和**：当别人明显听牌时还冒险打危险牌
4. **牌型单一**：只固定做一种牌型，不会灵活调整

掌握了这些基础知识，你就可以开始你的麻将之旅了！多加练习，熟能生巧。`,
    category: 'beginner',
    categoryName: '新手入门',
    author: '麻将小助手',
    date: '2025-12-15',
    readTime: '8分钟',
    image: 'https://readdy.ai/api/search-image?query=Beginner%20mahjong%20guide%20concept%20with%20tiles%20neatly%20organized%20on%20instructional%20layout%2C%20warm%20wooden%20background%2C%20soft%20educational%20lighting%2C%20clean%20minimal%20product%20photography%20with%20friendly%20approachable%20aesthetic&width=800&height=500&seq=article-beginner-01&orientation=landscape',
    tags: ['麻将入门', '新手教程', '麻将规则'],
  },
  {
    id: 'sichuan-mahjong-rules',
    title: '四川麻将血战到底：规则详解与实战技巧',
    excerpt: '四川麻将独特的血战到底和血流成河玩法，缺一门、刮风下雨等规则全解析，助你成为川麻高手。',
    content: `四川麻将（简称"川麻"）是中国最具特色的地方麻将玩法之一，以其独特的"血战到底"规则闻名全国。

## 核心特色规则

### 血战到底
四川麻将最标志性的规则。不同于传统麻将一人胡牌就结束，血战到底规则下，一家胡牌后其余三家继续对战，直到只剩最后一家或牌墙摸完。这意味着每局最多可以有三人胡牌，大大增加了游戏的刺激性和变数。

### 缺一门
玩家在开局时必须选择一门花色（万、条、筒其中之一）作为"缺门"，手牌中不能含有缺门花色的牌。如果在胡牌时手牌中仍有缺门花色的牌，则不能胡牌。这个规则迫使玩家在行牌过程中必须将缺门花色的牌全部打出。

### 刮风下雨
- **刮风**：明杠时，其他三家各支付一定分数
- **下雨**：暗杠时，其他三家各支付更多分数

这个规则让杠牌不仅是牌型升级的手段，更成为得分的利器。

## 牌型与番数

### 基础牌型
| 牌型 | 番数 | 说明 |
|------|------|------|
| 平胡 | 1番 | 基本胡牌 |
| 对对胡 | 2番 | 全部刻子 |
| 清一色 | 4番 | 同花色 |
| 七对 | 4番 | 七个对子 |
| 清对 | 8番 | 清一色+对对胡 |
| 龙七对 | 8番 | 七对中含一个杠 |
| 清七对 | 16番 | 清一色+七对 |
| 清龙七对 | 32番 | 清一色+龙七对 |

## 实战技巧

### 1. 缺门选择策略
- 优先选择手牌数量最少的花色作为缺门
- 如果三种花色数量相近，优先缺筒子（筒子顺子价值相对较低）
- 观察其他玩家的出牌判断他们的缺门

### 2. 防守策略
- 跟上家打相同花色的牌，减少被吃的机会
- 当有人明显在做大牌时，优先打出安全牌
- 注意记录已经打出的牌，判断哪些牌是危险的

### 3. 进攻策略
- 缺门清楚后，迅速确定胡牌方向
- 清一色是最常见的大牌路线，但也要根据手牌灵活调整
- 杠牌要算好收益，不要为杠而杠

掌握这些技巧后，多参与实战练习，你一定能在四川麻将的牌桌上游刃有余！`,
    category: 'strategy',
    categoryName: '玩法技巧',
    author: '川麻高手',
    date: '2025-11-28',
    readTime: '10分钟',
    image: 'https://readdy.ai/api/search-image?query=Sichuan%20mahjong%20strategic%20gameplay%20scene%20with%20dramatic%20spicy%20red%20aesthetic%2C%20tiles%20arranged%20on%20dark%20green%20felt%2C%20warm%20intense%20lighting%2C%20dynamic%20composition%2C%20professional%20product%20photography%20with%20bold%20contrast&width=800&height=500&seq=article-sichuan-02&orientation=landscape',
    tags: ['四川麻将', '血战到底', '麻将技巧'],
  },
  {
    id: 'mahjong-online-benefits',
    title: '在线玩麻将的五大优势：为什么越来越多人选择线上麻将',
    excerpt: '从便捷性到社交功能，解析在线麻将游戏的独特魅力，了解为什么数百万玩家已经转向线上麻将平台。',
    content: `随着互联网技术的发展，在线麻将游戏已经成为越来越多麻将爱好者的首选。让我们来看看在线玩麻将到底有哪些独特优势。

## 1. 随时随地的便捷性

在线麻将最大的优势就是无需等待凑齐四人。无论你是在家里、办公室休息时间、通勤路上，只要打开手机或电脑，就能立刻开始一局麻将。不需要预约场地，不需要约朋友，真正实现了"想玩就玩"。

## 2. 丰富的玩法选择

线下麻将通常只能玩本地流行的一两种规则。但在在线麻将平台上，你可以体验到：
- 四川麻将血战到底
- 广东麻将推倒胡
- 上海麻将清混碰
- 台湾十六张麻将
- 日本立直麻将
- 各种创新玩法

一个平台就能满足你对各种麻将玩法的好奇心。

## 3. 智能匹配与公平性

在线麻将使用智能匹配系统，根据你的水平自动匹配实力相当的对手。这避免了线下麻将中常见的"虐菜"或"被虐"问题，让每局比赛都充满悬念和乐趣。

同时，系统自动计算番数、自动发牌和洗牌，杜绝了作弊可能，保证游戏的绝对公平。

## 4. 新手上手友好

对于麻将新手来说，在线麻将是最好的学习方式：
- 新手教程引导你一步步学习规则
- 提示系统在你犹豫时给出建议
- 自动计番让你不用担心算错
- 回放功能让你复盘自己的对局
- AI练习模式让你无压力练手

## 5. 社交与竞技

在线麻将不只是打牌，更是一个社交平台：
- 实时语音聊天，边打边聊
- 好友系统，随时约战好友
- 排行榜系统，挑战自我
- 定期比赛，赢取奖品
- 麻将社区，交流经验

## 在线麻将适合你吗？

如果你是一个麻将爱好者，或者对麻将感兴趣但还没有尝试过，现在就是开始在线麻将的最佳时机。免费注册、即开即玩，你会发现一个全新的麻将世界在等着你！`,
    category: 'guide',
    categoryName: '游戏指南',
    author: '麻将小助手',
    date: '2025-12-08',
    readTime: '6分钟',
    image: 'https://readdy.ai/api/search-image?query=Modern%20online%20mahjong%20gaming%20experience%20on%20sleek%20digital%20device%2C%20warm%20cozy%20home%20environment%2C%20soft%20ambient%20lighting%2C%20clean%20minimal%20tech%20lifestyle%20photography%20with%20comfortable%20aesthetic&width=800&height=500&seq=article-online-03&orientation=landscape',
    tags: ['在线麻将', '麻将优势', '游戏指南'],
  },
  {
    id: 'mahjong-tiles-reading',
    title: '麻将读牌术：如何通过对手出牌判断牌型',
    excerpt: '中高级玩家必备技能——读牌术。学会从对手的出牌顺序和选择中，推测他们的手牌和意图。',
    content: `读牌是麻将进阶的核心技能。高水平的麻将玩家不仅仅是打自己的牌，更是在不断地"读"对手的牌。

## 什么是读牌术

读牌术是指通过观察对手的出牌行为（打什么牌、什么时候打、怎么打），来推测对手手牌组成和战术意图的技巧。

## 基础读牌技巧

### 1. 出牌顺序分析
对手出牌的顺序透露了很多信息：
- 先打字牌（东西南北中发白）→ 可能在做清一色或混一色
- 先打中间牌（四五六）→ 手牌比较散，还在组牌阶段
- 一直不打某个花色 → 那个花色可能是他的主攻方向
- 突然改变出牌习惯 → 可能已经听牌了

### 2. 碰牌后的出牌
- 碰牌后打出边张 → 在做刻子类牌型（对对胡方向）
- 碰牌后打出中间张 → 可能在做混一色或清一色
- 碰牌后打出同花色 → 缺门调整

### 3. 吃牌的行为分析
- 频繁吃上家的牌 → 顺子类牌型（平胡方向）
- 只吃特定花色的牌 → 在做清一色
- 有吃不吃 → 可能有更好的牌型考虑

## 高级读牌技巧

### "听牌信号"识别
当对手表现出以下行为时，很可能已经听牌：
1. 出牌速度明显加快或明显变慢
2. 不再吃碰（除非是自摸关键牌）
3. 表情变化（紧张、专注、或故作轻松）
4. 手指在牌上停留时间变长

### "危险牌"判断
- 对手一直不打的花色 → 该花色的生张危险
- 对手刚刚碰过的牌旁边数字 → 可能要组成顺子
- 对手频繁打出的花色 → 该花色相对安全
- 场面上出现较少的牌 → 大概率在对手手中

## 实战案例分析

### 案例：对手在做清一色
**观察**：对手只打条子和筒子，万子一张不打
**推断**：在做万子清一色
**对策**：优先处理手中的万子牌，避免后期打出被胡

### 案例：对手已听牌
**观察**：对手连续三轮快速出牌，且表情专注
**推断**：已经听牌，等待胡牌
**对策**：打安全牌，放弃进攻，防守为主

读牌术需要大量实战练习才能掌握。建议新手从记录对手出牌开始，逐步培养读牌意识。`,
    category: 'strategy',
    categoryName: '玩法技巧',
    author: '牌桌老手',
    date: '2025-10-20',
    readTime: '12分钟',
    image: 'https://readdy.ai/api/search-image?query=Strategic%20mahjong%20tile%20reading%20concept%20with%20focused%20player%20perspective%2C%20warm%20dramatic%20lighting%2C%20intense%20concentration%20mood%2C%20dark%20green%20felt%20with%20tiles%2C%20cinematic%20product%20photography%20with%20shallow%20depth%20of%20field&width=800&height=500&seq=article-reading-04&orientation=landscape',
    tags: ['读牌技巧', '麻将进阶', '策略分析'],
  },
  {
    id: 'mahjong-culture-history',
    title: '麻将的历史与文化：从古代到现代的国粹传承',
    excerpt: '探寻麻将的起源与发展历程，了解这项被誉为"中国国粹"的智力游戏背后的深厚文化底蕴。',
    content: `麻将被誉为中国的"国粹"之一，它不仅是一种娱乐方式，更承载着丰富的文化内涵和历史传承。

## 麻将的起源

关于麻将的起源，有多种说法。最广为流传的是起源于明朝的"叶子戏"和"马吊牌"。经过数百年的演变，在清朝道光年间（约1850年左右），现代麻将在宁波地区基本定型。

### 麻将名称的由来
"麻将"一词有多种解释：
- 一说来自"麻雀"，因为麻将牌碰撞的声音像麻雀叫声
- 一说来自宁波方言中"麻雀"的发音
- 还有说法认为是"马吊"的谐音演变

## 麻将在中国的传播

### 晚清民国：从地方到全国
19世纪末到20世纪初，麻将迅速从江浙地区传播到全国各地，成为各阶层都喜爱的娱乐活动。无论是达官贵人还是平民百姓，都能在麻将桌上找到乐趣。

### 现代发展：从线下到线上
进入21世纪，互联网的普及让麻将迎来了新的发展机遇。在线麻将平台的出现，让这种传统游戏焕发出新的活力：
- 1998年：最早的网络麻将游戏出现
- 2005年：QQ麻将等大型平台上线
- 2010年后：移动互联网让手游麻将蓬勃发展
- 至今：在线麻将已成为最受欢迎的线上游戏之一

## 麻将的国际影响

麻将不仅在中国流行，还传播到了世界各地：
- **日本**：发展出独特的立直麻将体系，成为竞技麻将的重要分支
- **美国**：1920年代传入，形成美式麻将规则
- **欧洲**：各国麻将协会定期举办比赛
- **世界麻将组织**：致力于推广标准化竞技麻将

## 麻将的文化价值

### 智力锻炼
麻将被称为"桌上太极拳"，是因为它能够锻炼人的：
- 记忆力（记住已出的牌）
- 逻辑推理（判断对手牌型）
- 策略规划（制定胡牌路线）
- 风险决策（进攻还是防守）

### 社交纽带
- 麻将桌是中国人社交的重要场所
- "三缺一"是中文里最著名的社交邀请之一
- 一局麻将往往能拉近人与人之间的距离

### 文化符号
麻将已经深深融入中国文化：
- 影视作品中频繁出现的麻将场景
- "碰"、"吃"、"杠"等麻将术语被广泛使用
- 麻将牌的设计本身就是中国传统艺术的体现

## 麻将的未来

随着科技的发展，麻将会继续进化。AI麻将教练、VR麻将体验、全球联网比赛...麻将的未来充满无限可能。但无论如何变化，这项凝聚了中国人智慧的游戏，都将继续传承下去。`,
    category: 'culture',
    categoryName: '麻将文化',
    author: '文化研究者',
    date: '2025-09-15',
    readTime: '10分钟',
    image: 'https://readdy.ai/api/search-image?query=Antique%20vintage%20Chinese%20mahjong%20set%20with%20hand%20carved%20bone%20tiles%20on%20aged%20wooden%20table%2C%20warm%20historical%20museum%20lighting%2C%20rich%20cultural%20atmosphere%2C%20traditional%20Chinese%20aesthetic%2C%20artistic%20heritage%20photography&width=800&height=500&seq=article-culture-05&orientation=landscape',
    tags: ['麻将历史', '麻将文化', '中国国粹'],
  },
  {
    id: 'mahjong-winning-tips',
    title: '麻将必胜十大口诀：高手不传的秘密',
    excerpt: '整理麻将高手多年经验总结的十大必胜口诀，从起手到胡牌，每一条都能帮你提升胜率。',
    content: `麻将桌上的高手们往往有一些不成文的"口诀"，这些口诀凝聚了无数对局的经验和教训。今天我们就来揭秘麻将高手的十大必胜口诀。

## 十大必胜口诀

### 口诀一：起手看三色，定缺要果断
开局第一件事就是判断三种花色的分布。哪个花色少就果断缺哪个，犹豫不决只会让自己陷入被动。

### 口诀二：幺九先出手，中间留一留
没有特殊需求的情况下，优先处理边张（一和九），中间的牌更容易组成顺子。

### 口诀三：有碰不急于，看准再下手
碰牌要三思：碰了这个牌是否真的对你的牌型有帮助？会不会暴露你的意图？

### 口诀四：听牌要低调，动作要自然
一旦听牌，保持正常的出牌节奏。突然改变习惯会让对手警觉。

### 口诀五：宁弃不和，安全第一
当判断有人在做大牌时，宁可放弃这一局也不要冒险。留得青山在，不怕没柴烧。

### 口诀六：跟打跟打再跟打
不确定打什么的时候，跟着上家打是最安全的选择，尤其在有危险牌的时候。

### 口诀七：多看牌面，少看手牌
养成习惯：每轮先把桌面上的牌扫一遍，再看自己的手牌。信息就是力量。

### 口诀八：大牌慢慢做，小牌快快胡
做大牌型需要耐心，不要在条件不成熟时强行做大。有时候快速胡一把小牌比憋着做大牌更好。

### 口诀九：心态要平稳，不骄不躁
无论是连胜还是连败，保持平和的心态。情绪波动是麻将最大的敌人。

### 口诀十：复盘是进步的阶梯
每局结束后花几分钟回顾：哪些决策是对的？哪些是错的？下次该怎么改进？

## 实战应用

这些口诀不是死板的规则，而是需要在实战中灵活运用的指导思想。建议新手可以先记住前五条，在实战中刻意练习，等到熟练掌握后再学习后五条。

记住，麻将虽是小技，但其中蕴含的智慧却可以受用终身。`,
    category: 'strategy',
    categoryName: '玩法技巧',
    author: '麻将大师',
    date: '2025-08-22',
    readTime: '7分钟',
    image: 'https://readdy.ai/api/search-image?query=Mahjong%20winning%20strategy%20concept%20with%20elegant%20golden%20tiles%20and%20lucky%20charms%20on%20deep%20green%20felt%2C%20warm%20prosperous%20lighting%2C%20fortune%20and%20skill%20aesthetic%2C%20refined%20product%20photography%20with%20auspicious%20atmosphere&width=800&height=500&seq=article-tips-06&orientation=landscape',
    tags: ['麻将口诀', '必胜技巧', '高手经验'],
  },
];